import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import * as Icons from "@nova/component-icons"
import { WorkspaceTextInput } from "@nova/component-inline-text-input"
import {
    PDFButtonID,
    selectBorderData,
    selectData,
    selectEnabled,
    selectGridData,
    selectPageSize,
    setBorderData,
    setData,
    setGridData,
    toggleData,
} from "../PDFSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useTranslation } from "@nova/use-translation"
import { selectFireById } from "features/entity-caching/fires/selectors"
import store from "integrations/redux/store"
import { zoomToArea } from "features/map/control/slice"
import { BORDER_THICKNESS } from "../PDFSnapshotContainer"
import { GridData, getLinePoints } from "../PDFAnnotations/PDFCoordinateNotations"
import { CRS } from "@nova/component-crs-selector"
import { LegendItem } from "../PDFLegend/LegendItems/useLegendItems"
import { formatShortDate } from "@nova/format"
import { PDFPanelSettings } from "./PDFPanelSettings"
import { InspectorPanelKeyValueToggle } from "features/map/inspector-panel/InspectorPanelKeyValueToggle"
import { ButtonTertiary } from "@nova/component-btn"
import { PDFFeatures } from "./buttons/PDFFeatures"
import { PDFLogos } from "./buttons/PDFLogos"
import { getViewport, useViewport } from "features/map/GlobalViewState"
import { selectActivatedFireId } from "features/map/control/selectors"

type Button = {
    id: PDFButtonID
    label: string
    Icon?: FC
    Menu?: FC<{
        legendItems: LegendItem[]
        toggle: (id: string) => void
        enabledLegendItems: LegendItem[]
        excludedLegendItems: LegendItem[]
        maxLegendItems: number
        hasExceededMax: boolean
    }>
    onToggle?: () => void
    excludedVisibilityToggle?: boolean
    showIconOnButton?: boolean
}

const GRID_LINES_ENABLED = true

const getValidRange = (
    data: GridData,
    tries: number,
    prevDirection: 0 | 1 | 2,
    crs?: CRS
): GridData | null => {
    if (tries > 15) {
        return null
    }
    const snapshotEl = document.getElementById("pdf-snapshot")
    if (!snapshotEl) return null
    const { top, left, width, height } = snapshotEl.getBoundingClientRect()
    const minX = left + BORDER_THICKNESS,
        minY = top + BORDER_THICKNESS,
        maxX = left + width - BORDER_THICKNESS,
        maxY = top + height - BORDER_THICKNESS
    const linePoints = getLinePoints(
        [
            [minX, minY],
            [maxX, minY],
            [maxX, maxY],
            [minX, maxY],
        ],
        getViewport(),
        data,
        crs
    )
    if (linePoints.length < 4) {
        if (prevDirection === 2) {
            return null
        }
        return getValidRange(
            {
                x: data.x / 10,
                y: data.y / 10,
                offsetX: data.offsetX,
                offsetY: data.offsetY,
            },
            tries + 1,
            1
        )
    } else if (linePoints.length > 41) {
        if (prevDirection === 1) {
            return null
        }
        return getValidRange(
            {
                x: data.x * 10,
                y: data.y * 10,
                offsetX: data.offsetX,
                offsetY: data.offsetY,
            },
            tries + 1,
            2
        )
    } else return data
}

export const getDevicePPI = () => {
    const el = document.createElement("div")
    el.style.width = "1in"
    document.body.appendChild(el)

    const dpi = el.getBoundingClientRect().width

    el.remove()
    return dpi
}

export const onBorderTickToggle = (crs?: CRS) => {
    if (crs) {
        const range = getValidRange({ x: 1, y: 1, offsetX: 0, offsetY: 0 }, 0, 0, crs)
        if (!range) return
        return store.dispatch(setBorderData({ ...range }))
    }
    const enabled = selectEnabled()(store.getState())
    if (enabled.gridlines && !crs) {
        const gridData = selectGridData()(store.getState())
        const range = getValidRange(gridData, 0, 0, crs)
        if (!range) return
        store.dispatch(setBorderData({ ...range }))
    } else {
        const borderData = selectBorderData()(store.getState())
        const range = getValidRange(borderData, 0, 0, crs)
        if (!range) return
        store.dispatch(setBorderData({ ...range }))
    }
}

export const onGridLineToggle = (crs?: CRS) => {
    if (crs) {
        const range = getValidRange({ x: 1, y: 1, offsetX: 0, offsetY: 0 }, 0, 0, crs)
        if (!range) return
        return store.dispatch(setGridData({ ...range }))
    }
    const enabled = selectEnabled()(store.getState())
    if (enabled.borderticks) {
        const borderData = selectBorderData()(store.getState())
        const range = getValidRange(borderData, 0, 0, crs)
        if (!range) return
        store.dispatch(setGridData({ ...range }))
    } else {
        const gridData = selectGridData()(store.getState())
        const range = getValidRange(gridData, 0, 0, crs)
        if (!range) return
        store.dispatch(setGridData({ ...range }))
    }
}

const buttons: Button[] = [
    {
        id: "title",
        label: "Titles",
        Icon: Icons.PDFTitles,
        Menu: () => {
            const { t } = useTranslation()
            const dispatch = useAppDispatch()
            const title = useAppSelector(selectData("title"))
            const subtitle = useAppSelector(selectData("subtitle"))
            const date = useAppSelector(selectData("date"))
            const fireId = useAppSelector(selectActivatedFireId)
            const fire = useAppSelector(selectFireById(fireId))!
            const [hasEditedTitle, setHasEditedTitle] = useState(false)
            const [hasEditedSubtitle, setHasEditedSubtitle] = useState(false)
            const [hasEditedDate, setHasEditedDate] = useState(false)
            return (
                <div className="flex flex-col gap-4 w-full">
                    <div>
                        <small>{t("Title")}</small>
                        <WorkspaceTextInput
                            name="title"
                            value={!title && !hasEditedTitle ? fire.name : title}
                            handleChange={(title) => {
                                if (!hasEditedTitle) setHasEditedTitle(true)
                                dispatch(setData({ type: "title", data: title }))
                            }}
                            className="mt-2 text-gray-0"
                            inputClassName="text-[14px]"
                        />
                    </div>
                    <div>
                        <small>{t("Subtitle")}</small>
                        <WorkspaceTextInput
                            name="Subtitle"
                            value={!subtitle && !hasEditedSubtitle ? fire.name : subtitle}
                            handleChange={(title) => {
                                if (!hasEditedSubtitle) setHasEditedSubtitle(true)
                                dispatch(setData({ type: "subtitle", data: title }))
                            }}
                            className="mt-2 text-gray-0"
                            inputClassName="text-[14px]"
                        />
                    </div>
                    <div>
                        <small>{t("Date")}</small>
                        <WorkspaceTextInput
                            name="Date"
                            value={
                                !date && !hasEditedDate
                                    ? formatShortDate({ value: new Date() })
                                    : date
                            }
                            handleChange={(title) => {
                                if (!hasEditedDate) setHasEditedDate(true)
                                dispatch(setData({ type: "date", data: title }))
                            }}
                            className="mt-2 text-gray-0"
                            inputClassName="text-[14px]"
                        />
                    </div>
                </div>
            )
        },
    },
    {
        id: "features",
        label: "Features",
        Icon: Icons.PDFFeatures,
        Menu: PDFFeatures,
    },
    {
        id: "inset",
        label: "Inset",
    },
    {
        id: "scalebar",
        label: "Scale Bar",
    },

    {
        id: "ratio",
        label: "Ratio",
        Icon: Icons.PDFRatio,
        Menu: () => {
            const dispatch = useAppDispatch()
            const { t } = useTranslation()
            const size = useAppSelector(selectPageSize())
            const viewState = useViewport()
            const displayedRatio = useAppSelector(selectData("displayedRatio"))
            const ratio = useMemo(() => parseInt(displayedRatio).toFixed(0), [displayedRatio])
            const [isEditing, setIsEditing] = useState(false)
            const [temporaryRatio, setTemporaryRatio] = useState(ratio)

            const [errorInputBorder, setErrorInputBorder] = useState(false)
            const [errorMessage, setErrorMessage] = useState("")

            const getRatioForZoom = useCallback(
                (snapshotEl: HTMLElement, zoom: number) => {
                    const K =
                        getDevicePPI() *
                        (snapshotEl.getBoundingClientRect().height /
                            getDevicePPI() /
                            parseFloat(size.split('"')[0])) *
                        (1 / 0.0254) *
                        (78271.51696 * Math.cos((viewState.latitude * Math.PI) / 180))
                    return Math.floor(K / Math.pow(2, zoom))
                },
                [size, viewState]
            )
            const getZoomForRatio = useCallback(
                (snapshotEl: HTMLElement, ratio: number) => {
                    const K =
                        getDevicePPI() *
                        (snapshotEl.getBoundingClientRect().height /
                            getDevicePPI() /
                            parseFloat(size.split('"')[0])) *
                        (1 / 0.0254) *
                        (78271.51696 * Math.cos((viewState.latitude * Math.PI) / 180))
                    return Math.log2(K / ratio)
                },
                [size, viewState.latitude]
            )

            const setRatio = useCallback(() => {
                const num = parseInt(temporaryRatio) || 0
                if (`${num}` === ratio) {
                    setErrorInputBorder(false)
                    setErrorMessage("")
                    setTemporaryRatio(ratio)
                    setIsEditing(false)
                    return
                }
                if (isNaN(num)) {
                    setErrorInputBorder(true)
                    setErrorMessage("Ratio must be a number")
                    return
                }
                const snapshotEl = document.getElementById("pdf-snapshot")
                if (!snapshotEl || num <= 0) {
                    setErrorInputBorder(true)
                    setErrorMessage("Ratio must be a positive number")
                    return
                }

                const minRatio = getRatioForZoom(snapshotEl, 24)
                const maxRatio = getRatioForZoom(snapshotEl, 4)

                if (num < minRatio) {
                    setErrorInputBorder(true)
                    setErrorMessage(`Ratio is too low. Please enter a value above ${minRatio}`)
                    return
                }
                if (num > maxRatio) {
                    setErrorInputBorder(true)
                    setErrorMessage(`Ratio is too high. Please enter a value below ${maxRatio}`)
                    return
                }

                setErrorInputBorder(false)
                setErrorMessage("")
                setTemporaryRatio(`${num}`)
                let zoom = getZoomForRatio(snapshotEl, num)
                zoom = Math.max(4, Math.min(zoom, 24))
                dispatch(setData({ type: "ratio", data: `${num}` }))
                dispatch(zoomToArea({ zoom }))
            }, [dispatch, getRatioForZoom, getZoomForRatio, ratio, temporaryRatio])

            const handleFocus = useCallback(() => {
                if (errorInputBorder) setErrorInputBorder(false)
                setIsEditing(true)
            }, [errorInputBorder])
            const handleKeyDown = useCallback(
                (e: React.KeyboardEvent) => {
                    if (e.key.toLowerCase() === "enter") {
                        e.preventDefault()
                        setRatio()
                    }
                },
                [setRatio]
            )
            useEffect(() => {
                // We defer changing isEditing until the new value is read.
                // This prevents a weird number jump
                // Also clear errors
                setIsEditing(false)
                setTemporaryRatio(ratio)
                setErrorInputBorder(false)
                setErrorMessage("")
            }, [ratio])

            return (
                <div className="flex flex-col gap-1">
                    <small>{t("Ratio")}</small>
                    <div className="flex flex-row gap-2 mt-1 items-center w-full">
                        <h5 className="text-gray-0">1:</h5>
                        <WorkspaceTextInput
                            name="ratio"
                            value={isEditing ? temporaryRatio : ratio}
                            handleChange={setTemporaryRatio}
                            onFocus={handleFocus}
                            onKeyDown={handleKeyDown}
                            className="text-gray-0 w-full"
                            inputClassName={`text-[14px] ${errorInputBorder ? "border border-red-500" : ""}`}
                        />
                    </div>
                    {errorMessage && <small className="text-red-500">{errorMessage}</small>}
                    <ButtonTertiary title="Confirm" className="mt-1 w-full" onClick={setRatio} />
                </div>
            )
        },
    },
    {
        id: "text1",
        label: "Text Box",
        Icon: Icons.PDFText,
        Menu: () => {
            const { t } = useTranslation()
            const dispatch = useAppDispatch()
            const text = useAppSelector(selectData("text1"))
            const textareaRef = useRef<HTMLTextAreaElement>(null)

            const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                const newValue = e.target.value
                dispatch(setData({ type: "text1", data: newValue }))

                // Resize the textarea
                if (textareaRef.current) {
                    textareaRef.current.style.height = "auto"
                    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
                }
            }

            // Resize on mount or when value changes (e.g. pre-filled text)
            useEffect(() => {
                if (textareaRef.current) {
                    textareaRef.current.style.height = "auto"
                    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
                }
            }, [text])

            return (
                <div className="flex flex-col gap-2">
                    <small>{t("Description")}</small>
                    <textarea
                        ref={textareaRef}
                        className="bg-gray-700 text-gray-0 rounded-lg border-none h-auto resize-none text-[14px] scrollable-area max-h-[500px] min-h-[300px] overflow-auto"
                        value={text}
                        onChange={handleChange}
                    />
                </div>
            )
        },
    },
    {
        id: "text2",
        label: "Meta Data",
        Icon: Icons.PDFMetaData,
        Menu: () => {
            const dispatch = useAppDispatch()
            const enabled = useAppSelector(selectEnabled())

            const projection = useAppSelector(selectData("projection"))
            const datum = useAppSelector(selectData("datum"))
            const createdAt = useAppSelector(selectData("createdAt"))
            const createdBy = useAppSelector(selectData("createdBy"))
            const operator = useAppSelector(selectData("operator"))

            const hasFirstRender = useRef(false)
            useEffect(() => {
                hasFirstRender.current = true
            }, [])

            return (
                <div className="flex flex-col gap-[8px] grow w-full">
                    <InspectorPanelKeyValueToggle
                        keyTitle="Projection"
                        isToggled={enabled["projection"]}
                        setIsToggled={() => dispatch(toggleData("projection"))}
                        toggleClassName="justify-between"
                        Tag="small"
                    />
                    {enabled.projection && (
                        <WorkspaceTextInput
                            value={projection}
                            name="projection"
                            handleChange={(value) =>
                                dispatch(setData({ type: "projection", data: value }))
                            }
                            className="text-gray-0"
                            inputClassName="text-[14px]"
                            autoFocus={hasFirstRender.current}
                        />
                    )}
                    <div className="border-b border-b-gray-700" />
                    <InspectorPanelKeyValueToggle
                        keyTitle="Datum"
                        isToggled={enabled["datum"]}
                        setIsToggled={() => dispatch(toggleData("datum"))}
                        toggleClassName="justify-between"
                        Tag="small"
                    />
                    {enabled.datum && (
                        <WorkspaceTextInput
                            value={datum}
                            name="datum"
                            handleChange={(value) =>
                                dispatch(setData({ type: "datum", data: value }))
                            }
                            className="text-gray-0"
                            inputClassName="text-[14px]"
                            autoFocus={hasFirstRender.current}
                        />
                    )}
                    <div className="border-b border-b-gray-700" />
                    <InspectorPanelKeyValueToggle
                        keyTitle="Date Created"
                        isToggled={enabled["createdAt"]}
                        setIsToggled={() => dispatch(toggleData("createdAt"))}
                        toggleClassName="justify-between"
                        Tag="small"
                    />
                    {enabled.createdAt && (
                        <WorkspaceTextInput
                            value={createdAt}
                            name="createdAt"
                            handleChange={(value) =>
                                dispatch(setData({ type: "createdAt", data: value }))
                            }
                            className="text-gray-0"
                            inputClassName="text-[14px]"
                            autoFocus={hasFirstRender.current}
                        />
                    )}
                    <div className="border-b border-b-gray-700" />
                    <InspectorPanelKeyValueToggle
                        keyTitle="Map Produced by"
                        isToggled={enabled["createdBy"]}
                        setIsToggled={() => dispatch(toggleData("createdBy"))}
                        toggleClassName="justify-between"
                        Tag="small"
                    />
                    {enabled.createdBy && (
                        <WorkspaceTextInput
                            value={createdBy}
                            name="createdBy"
                            handleChange={(value) =>
                                dispatch(setData({ type: "createdBy", data: value }))
                            }
                            className="text-gray-0"
                            inputClassName="text-[14px]"
                            autoFocus={hasFirstRender.current}
                        />
                    )}
                    <div className="border-b border-b-gray-700" />
                    <InspectorPanelKeyValueToggle
                        keyTitle="Camera Operator / Type"
                        isToggled={enabled["operator"]}
                        setIsToggled={() => dispatch(toggleData("operator"))}
                        toggleClassName="justify-between"
                        Tag="small"
                    />
                    {enabled.operator && (
                        <WorkspaceTextInput
                            value={operator}
                            placeholder="Full Name"
                            name="operator"
                            handleChange={(value) =>
                                dispatch(setData({ type: "operator", data: value }))
                            }
                            className="text-gray-0"
                            inputClassName="text-[14px]"
                            autoFocus={hasFirstRender.current}
                        />
                    )}
                    <div className="border-b border-b-gray-700" />
                </div>
            )
        },
    },

    {
        id: "logos",
        label: "Logos",
        Icon: Icons.PDFLogos,
        Menu: PDFLogos,
    },
    {
        id: "qrcode",
        label: "QR Code",
    },
    {
        id: "compass",
        label: "Compass",
    },
]

if (GRID_LINES_ENABLED) {
    buttons.push({
        id: "gridlines",
        label: "Grid Lines",
        Icon: Icons.PDFGridLines,
        Menu: () => {
            const { t } = useTranslation()
            const dispatch = useAppDispatch()
            const gridData = useAppSelector(selectGridData())
            return (
                <div className="flex flex-col gap-[8px] w-full pr-2">
                    <small>{t("Interval")}</small>
                    <div className="flex gap-2">
                        <h5 className="my-auto text-gray-0">{t("X")}</h5>
                        <WorkspaceTextInput
                            value={gridData.x}
                            name="gridX"
                            handleChange={(value) =>
                                dispatch(setGridData({ ...gridData, x: parseFloat(value) }))
                            }
                            type="number"
                            className="w-[100px] text-gray-0"
                            min={0}
                            step={0.05}
                            inputClassName="text-[14px]"
                            data-custom-number-input="true"
                        />
                        <h5 className="my-auto text-gray-0">{t("Map Units")}</h5>
                    </div>
                    <div className="flex gap-2">
                        <h5 className="my-auto text-gray-0">{t("Y")}</h5>
                        <WorkspaceTextInput
                            value={gridData.y}
                            name="gridY"
                            handleChange={(value) =>
                                dispatch(setGridData({ ...gridData, y: parseFloat(value) }))
                            }
                            type="number"
                            className="w-[100px] text-gray-0"
                            step={0.05}
                            min={0}
                            inputClassName="text-[14px]"
                            data-custom-number-input="true"
                        />
                        <h5 className="my-auto text-gray-0">{t("Map Units")}</h5>
                    </div>
                    <small className="mt-4">{t("Offset")}</small>
                    <div className="flex gap-2">
                        <h5 className="my-auto text-gray-0">{t("X")}</h5>
                        <WorkspaceTextInput
                            value={gridData.offsetX}
                            name="gridOffsetX"
                            handleChange={(value) =>
                                dispatch(setGridData({ ...gridData, offsetX: parseFloat(value) }))
                            }
                            type="number"
                            className="w-[100px] text-gray-0"
                            step={0.05}
                            inputClassName="text-[14px]"
                            data-custom-number-input="true"
                        />
                        <h5 className="my-auto text-gray-0">{t("Map Units")}</h5>
                    </div>
                    <div className="flex gap-2">
                        <h5 className="my-auto text-gray-0">{t("Y")}</h5>
                        <WorkspaceTextInput
                            value={gridData.offsetY}
                            name="gridOffsetY"
                            handleChange={(value) =>
                                dispatch(setGridData({ ...gridData, offsetY: parseFloat(value) }))
                            }
                            type="number"
                            step={0.05}
                            className="w-[100px] text-gray-0"
                            inputClassName="text-[14px]"
                            data-custom-number-input="true"
                        />
                        <h5 className="my-auto text-gray-0">{t("Map Units")}</h5>
                    </div>
                </div>
            )
        },
        onToggle: onGridLineToggle,
    })
    buttons.push({
        id: "borderticks",
        label: "Border Ticks",
        Icon: Icons.PDFBorderTicks,
        Menu: () => {
            const { t } = useTranslation()
            const dispatch = useAppDispatch()
            const borderData = useAppSelector(selectBorderData())
            return (
                <div className="flex flex-col gap-[8px] w-full pr-2">
                    <small>{t("Interval")}</small>
                    <div className="flex gap-2">
                        <h5 className="my-auto text-gray-0">{t("X")}</h5>
                        <WorkspaceTextInput
                            value={borderData.x}
                            name="gridX"
                            handleChange={(value) =>
                                dispatch(setBorderData({ ...borderData, x: parseFloat(value) }))
                            }
                            type="number"
                            className="w-[100px] text-gray-0"
                            min={0}
                            step={0.05}
                            inputClassName="text-[14px]"
                        />
                        <h5 className="my-auto text-gray-0">{t("Map Units")}</h5>
                    </div>
                    <div className="flex gap-2">
                        <h5 className="my-auto text-gray-0">{t("Y")}</h5>
                        <WorkspaceTextInput
                            value={borderData.y}
                            name="gridY"
                            handleChange={(value) =>
                                dispatch(setBorderData({ ...borderData, y: parseFloat(value) }))
                            }
                            type="number"
                            className="w-[100px] text-gray-0"
                            step={0.05}
                            min={0}
                            inputClassName="text-[14px]"
                        />
                        <h5 className="my-auto text-gray-0">{t("Map Units")}</h5>
                    </div>
                    <small className="mt-4">{t("Offset")}</small>
                    <div className="flex gap-2">
                        <h5 className="my-auto text-gray-0">{t("X")}</h5>
                        <WorkspaceTextInput
                            value={borderData.offsetX}
                            name="gridOffsetX"
                            handleChange={(value) =>
                                dispatch(
                                    setBorderData({ ...borderData, offsetX: parseFloat(value) })
                                )
                            }
                            type="number"
                            className="w-[100px] text-gray-0"
                            step={0.05}
                            inputClassName="text-[14px]"
                        />
                        <h5 className="my-auto text-gray-0">{t("Map Units")}</h5>
                    </div>
                    <div className="flex gap-2">
                        <h5 className="my-auto text-gray-0">{t("Y")}</h5>
                        <WorkspaceTextInput
                            value={borderData.offsetY}
                            name="gridOffsetY"
                            handleChange={(value) =>
                                dispatch(
                                    setBorderData({ ...borderData, offsetY: parseFloat(value) })
                                )
                            }
                            type="number"
                            step={0.05}
                            className="w-[100px] text-gray-0"
                            inputClassName="text-[14px]"
                        />
                        <h5 className="my-auto text-gray-0">{t("Map Units")}</h5>
                    </div>
                </div>
            )
        },
        onToggle: onBorderTickToggle,
    })
}

buttons.push({
    id: "settings",
    label: "Settings",
    Icon: Icons.SettingsCog,
    Menu: PDFPanelSettings,
    excludedVisibilityToggle: true,
    showIconOnButton: true,
})

export { buttons }
